import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { createSubmission } from '../../api/runs';
import { uploadFile } from '../../api/files';
import { ApiError } from '../../api/client';
import ErrorAlert from '../../components/ErrorAlert';
import type { ChallengeFormProps } from './TextChallengeForm';

const VideoChallengeForm: React.FC<ChallengeFormProps> = ({
  orgSlug,
  runId,
  challenge,
  alreadySubmitted,
  onSubmitted,
  submitAction,
}) => {
  const { t } = useTranslation('runs');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);
  // Track current object URL for cleanup on unmount.
  const activeUrlRef = useRef<string | null>(null);

  // Revoke object URL on unmount only — no setState here.
  useEffect(() => {
    return () => {
      if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
    };
  }, []);

  if (alreadySubmitted) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {t('participant.challenge.alreadySubmitted')}
      </Typography>
    );
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    // Revoke the previous object URL in the event handler (not during render).
    if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
    activeUrlRef.current = selected ? URL.createObjectURL(selected) : null;
    setPreviewUrl(activeUrlRef.current);
    setFile(selected);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setUploadProgress(0);
    try {
      const uploaded = await uploadFile(orgSlug, runId, file, setUploadProgress);
      setUploadProgress(100);
      const content = { video_url: uploaded.object_key };
      const result = submitAction
        ? await submitAction(content)
        : await createSubmission(orgSlug, runId, {
            run_challenge_id: challenge.id,
            content,
          });
      onSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && <ErrorAlert message={error} />}

        {previewUrl && (
          <Box
            component="video"
            controls
            playsInline
            src={previewUrl}
            aria-label={t('participant.challenge.video.preview')}
            sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1 }}
          />
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => captureRef.current?.click()}
            disabled={submitting}
          >
            {t('participant.challenge.video.capture')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => galleryRef.current?.click()}
            disabled={submitting}
          >
            {t('participant.challenge.video.gallery')}
          </Button>
        </Stack>

        {/* Hidden file inputs */}
        <input
          ref={captureRef}
          type="file"
          accept="video/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {uploadProgress !== null && (
          <Box>
            <Typography variant="caption">
              {t('participant.challenge.video.uploading')}
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!file || submitting}
        >
          {t('participant.challenge.submit')}
        </Button>
      </Stack>
    </Box>
  );
};

export default VideoChallengeForm;
